import { Box, HStack, Text, VStack } from "@chakra-ui/react";
import { useToast } from "@chakra-ui/toast";
import { ContentCopy as ContentCopyIcon } from "@mui/icons-material";
import { FC, useCallback } from "react";

import { UrlMappingMatchType } from "~/src/__relay_artifacts__/UrlMappingPreviewPanel_Query.graphql";
import { IconButton } from "~/src/components/common/IconButton";

import { matchTypeLabels } from "./urlMappingFormUtils";
import {
  QueryParamChange,
  UrlMappingResultDiff,
  diffUrlMappingResult,
} from "./urlMappingPreviewUtils";

type Props = {
  inputUrl: string;
  mappedUrl: string;
  matched: boolean;
  matchedRuleName?: string | null;
  matchedMatchType?: UrlMappingMatchType | null;
  matchedRuleActive?: boolean | null;
};

const urlBoxStyles = {
  bg: "#FAFAFA",
  border: "1px solid #E2E8F0",
  borderRadius: "4px",
  p: "10px 12px",
  fontSize: "11px",
  lineHeight: "18px",
  fontFamily: "Menlo, Monaco, Consolas, monospace",
  color: "#2D3748",
  wordBreak: "break-all" as const,
};

const UrlBlock: FC<{ label: string; url: string }> = ({ label, url }) => {
  const toast = useToast();

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(url);
    toast({ title: "コピーしました", status: "success", duration: 2000 });
  }, [toast, url]);

  return (
    <Box>
      <HStack justifyContent="space-between" mb="4px">
        <Text fontSize="11px" fontWeight="600" color="#4A5568">
          {label}
        </Text>
        <IconButton
          size="16px"
          label="コピー"
          icon={<ContentCopyIcon />}
          onClick={handleCopy}
        />
      </HStack>
      <Box {...urlBoxStyles}>{url}</Box>
    </Box>
  );
};

const QueryParamChangeRow: FC<{ change: QueryParamChange }> = ({ change }) => {
  if (change.type === "changed") {
    return (
      <HStack
        alignItems="flex-start"
        spacing="8px"
        fontSize="11px"
        fontFamily="Menlo, Monaco, Consolas, monospace"
        py="4px"
        borderBottom="1px solid #EDF2F7"
      >
        <Text color="#4A5568" minW="100px" fontWeight="600">
          {change.key}
        </Text>
        <Text
          color="#C53030"
          textDecoration="line-through"
          wordBreak="break-all"
        >
          {change.from}
        </Text>
        <Text color="#A0AEC0">→</Text>
        <Text color="#276749" fontWeight="600" wordBreak="break-all">
          {change.to}
        </Text>
      </HStack>
    );
  }

  if (change.type === "added") {
    return (
      <HStack
        fontSize="11px"
        fontFamily="Menlo, Monaco, Consolas, monospace"
        py="4px"
        borderBottom="1px solid #EDF2F7"
      >
        <Text color="#276749" fontWeight="600">
          + {change.key}={change.value}
        </Text>
      </HStack>
    );
  }

  return (
    <HStack
      fontSize="11px"
      fontFamily="Menlo, Monaco, Consolas, monospace"
      py="4px"
      borderBottom="1px solid #EDF2F7"
    >
      <Text color="#C53030" textDecoration="line-through">
        − {change.key}={change.value}
      </Text>
    </HStack>
  );
};

const ChangesSection: FC<{ diff: UrlMappingResultDiff }> = ({ diff }) => {
  const hasPathChange = diff.originChanged || diff.pathnameChanged;
  const hasQueryChange = diff.queryChanges.length > 0;

  if (!hasPathChange && !hasQueryChange) {
    return (
      <Text fontSize="12px" color="#718096" mt="12px">
        URLに変更はありません
      </Text>
    );
  }

  return (
    <Box mt="12px">
      <Text fontSize="11px" fontWeight="600" color="#4A5568" mb="6px">
        変更点
      </Text>
      <Box
        bg="white"
        border="1px solid #E2E8F0"
        borderRadius="4px"
        px="12px"
        py="4px"
      >
        {hasPathChange ? (
          <VStack align="stretch" spacing="6px" py="6px">
            {diff.originChanged ? (
              <HStack fontSize="11px" alignItems="flex-start">
                <Text color="#4A5568" minW="72px" fontWeight="600">
                  origin
                </Text>
                <Text
                  color="#C53030"
                  textDecoration="line-through"
                  wordBreak="break-all"
                  fontFamily="Menlo, Monaco, Consolas, monospace"
                >
                  {diff.inputOrigin}
                </Text>
                <Text color="#A0AEC0">→</Text>
                <Text
                  color="#276749"
                  fontWeight="600"
                  wordBreak="break-all"
                  fontFamily="Menlo, Monaco, Consolas, monospace"
                >
                  {diff.mappedOrigin}
                </Text>
              </HStack>
            ) : null}
            {diff.pathnameChanged ? (
              <HStack fontSize="11px" alignItems="flex-start">
                <Text color="#4A5568" minW="72px" fontWeight="600">
                  path
                </Text>
                <Text
                  color="#C53030"
                  textDecoration="line-through"
                  wordBreak="break-all"
                  fontFamily="Menlo, Monaco, Consolas, monospace"
                >
                  {diff.inputPathname}
                </Text>
                <Text color="#A0AEC0">→</Text>
                <Text
                  color="#276749"
                  fontWeight="600"
                  wordBreak="break-all"
                  fontFamily="Menlo, Monaco, Consolas, monospace"
                >
                  {diff.mappedPathname}
                </Text>
              </HStack>
            ) : null}
          </VStack>
        ) : null}
        {diff.queryChanges.map((change) => (
          <QueryParamChangeRow key={change.key} change={change} />
        ))}
      </Box>
    </Box>
  );
};

const matchedRuleSummary = (
  name: string | null | undefined,
  matchType: UrlMappingMatchType | null | undefined,
  active: boolean | null | undefined
): string | null => {
  if (!matchType) return null;
  const typeLabel =
    matchTypeLabels[matchType as keyof typeof matchTypeLabels] ?? matchType;
  const nameLabel = name?.trim() ? name.trim() : "(名称なし)";
  const activeLabel = active === false ? " · 無効" : "";
  return `${nameLabel} · ${typeLabel}${activeLabel}`;
};

export const UrlMappingPreviewResult: FC<Props> = ({
  inputUrl,
  mappedUrl,
  matched,
  matchedRuleName,
  matchedMatchType,
  matchedRuleActive,
}) => {
  const diff = diffUrlMappingResult(inputUrl, mappedUrl);
  const ruleSummary = matched
    ? matchedRuleSummary(matchedRuleName, matchedMatchType, matchedRuleActive)
    : null;

  return (
    <Box
      mt="4px"
      p="14px"
      borderRadius="6px"
      border="1px solid"
      borderColor={matched ? "#9AE6B4" : "#E2E8F0"}
      bg={matched ? "#F0FFF4" : "#F7FAFC"}
    >
      <HStack spacing="8px" mb={ruleSummary ? "4px" : "12px"}>
        <Box
          w="8px"
          h="8px"
          borderRadius="full"
          bg={matched ? "#38A169" : "#A0AEC0"}
          flexShrink={0}
        />
        <Text fontSize="13px" fontWeight="600" color="#2D3748">
          {matched ? "マッチあり" : "マッチなし"}
        </Text>
      </HStack>
      {ruleSummary ? (
        <Text fontSize="12px" color="#4A5568" mb="12px" pl="16px">
          {ruleSummary}
          {matchedRuleActive === false ? (
            <Text as="span" color="#C05621" display="block" mt="2px">
              このルールは無効です。本番反映前に有効にしてください。
            </Text>
          ) : null}
        </Text>
      ) : null}

      {matched ? (
        <VStack align="stretch" spacing="10px">
          <UrlBlock label="入力URL" url={inputUrl} />
          <UrlBlock label="結果URL" url={mappedUrl} />
          {inputUrl === mappedUrl ? (
            <Text fontSize="12px" color="#718096">
              ルールには一致しましたが、結果URLは入力と同じため変換はありません。
            </Text>
          ) : null}
          {diff ? <ChangesSection diff={diff} /> : null}
        </VStack>
      ) : (
        <VStack align="stretch" spacing="8px">
          <Text fontSize="12px" color="#718096">
            入力URLはどのルールにも一致しませんでした。
          </Text>
          <UrlBlock label="入力URL" url={inputUrl} />
        </VStack>
      )}
    </Box>
  );
};
